class PdpFeaturedProductsSlider extends HTMLElement {
  constructor() {
    super();

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerEnd = this.onPointerEnd.bind(this);
    this.onClick = this.onClick.bind(this);
    this.preventNativeDrag = this.preventNativeDrag.bind(this);
  }

  connectedCallback() {
    this.slider = this.querySelector('[data-pdp-featured-products-slider]');
    this.slides = Array.from(this.querySelectorAll('[data-pdp-featured-products-slide]'));
    this.prevButton = this.querySelector('[data-pdp-featured-products-prev]');
    this.nextButton = this.querySelector('[data-pdp-featured-products-next]');
    this.controls = this.querySelector('[data-pdp-featured-products-controls]');
    this.dots = Array.from(this.querySelectorAll('[data-pdp-featured-products-dot]'));

    if (!this.slider || this.slides.length < 2) return;

    this.slider.addEventListener('scroll', this.onScroll, { passive: true });
    this.slider.addEventListener('pointerdown', this.onPointerDown);
    this.slider.addEventListener('pointermove', this.onPointerMove);
    this.slider.addEventListener('pointerup', this.onPointerEnd);
    this.slider.addEventListener('pointercancel', this.onPointerEnd);
    this.slider.addEventListener('lostpointercapture', this.onPointerEnd);
    this.slider.addEventListener('click', this.onClick);
    this.slider.addEventListener('dragstart', this.preventNativeDrag);
    window.addEventListener('resize', this.onResize);
    this.prevButton?.addEventListener('click', () => this.goToPage(this.activePage - 1));
    this.nextButton?.addEventListener('click', () => this.goToPage(this.activePage + 1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goToPage(Number(dot.dataset.page)));
    });

    this.update();
  }

  disconnectedCallback() {
    if (!this.slider || this.slides.length < 2) return;

    this.slider.removeEventListener('scroll', this.onScroll);
    this.slider.removeEventListener('pointerdown', this.onPointerDown);
    this.slider.removeEventListener('pointermove', this.onPointerMove);
    this.slider.removeEventListener('pointerup', this.onPointerEnd);
    this.slider.removeEventListener('pointercancel', this.onPointerEnd);
    this.slider.removeEventListener('lostpointercapture', this.onPointerEnd);
    this.slider.removeEventListener('click', this.onClick);
    this.slider.removeEventListener('dragstart', this.preventNativeDrag);
    window.clearTimeout(this.clickResetTimeout);
    window.clearTimeout(this.settleTimeout);
    window.removeEventListener('resize', this.onResize);
  }

  onScroll() {
    window.requestAnimationFrame(() => this.update());
  }

  onResize() {
    this.update();
  }

  onPointerDown(event) {
    if (
      event.pointerType === 'touch' ||
      event.button !== 0 ||
      !event.isPrimary ||
      this.maxScrollLeft === 0
    ) {
      return;
    }

    this.pointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.scrollStartX = this.slider.scrollLeft;
    this.isDragging = false;
  }

  onPointerMove(event) {
    if (event.pointerId !== this.pointerId) return;

    const distanceX = event.clientX - this.pointerStartX;
    const distanceY = event.clientY - this.pointerStartY;

    if (!this.isDragging) {
      if (Math.abs(distanceX) < 6 || Math.abs(distanceX) <= Math.abs(distanceY)) return;

      this.isDragging = true;
      this.preventClick = true;
      this.slider.classList.add('is-dragging');
      this.slider.setPointerCapture(event.pointerId);
    }

    event.preventDefault();
    this.slider.scrollLeft = this.scrollStartX - distanceX;
  }

  onPointerEnd(event) {
    if (event.pointerId !== this.pointerId) return;

    const wasDragging = this.isDragging;

    this.pointerId = null;
    this.isDragging = false;

    if (!wasDragging) return;

    if (this.slider.hasPointerCapture(event.pointerId)) {
      this.slider.releasePointerCapture(event.pointerId);
    }

    this.settleToClosestSlide();
    window.clearTimeout(this.clickResetTimeout);
    this.clickResetTimeout = window.setTimeout(() => {
      this.preventClick = false;
    }, 0);
  }

  onClick(event) {
    if (!this.preventClick) return;

    event.preventDefault();
    this.preventClick = false;
    window.clearTimeout(this.clickResetTimeout);
  }

  preventNativeDrag(event) {
    event.preventDefault();
  }

  settleToClosestSlide() {
    const scrollLeft = this.slider.scrollLeft;
    const closestSlide = this.slides.reduce((closest, slide) => {
      return Math.abs(scrollLeft - this.getSlideSnapScrollLeft(slide)) <
        Math.abs(scrollLeft - this.getSlideSnapScrollLeft(closest))
        ? slide
        : closest;
    });

    this.smoothScrollTo(this.getSlideSnapScrollLeft(closestSlide));
  }

  smoothScrollTo(left) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.slider.classList.add('is-settling');
    this.slider.classList.remove('is-dragging');
    this.slider.scrollTo({
      left,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });

    window.clearTimeout(this.settleTimeout);
    this.settleTimeout = window.setTimeout(() => {
      this.slider.classList.remove('is-settling');
    }, reducedMotion ? 0 : 400);
  }

  get activePage() {
    const scrollLeft = this.slider.scrollLeft;

    return this.pageStarts.reduce((closestPage, slideIndex, pageIndex) => {
      const closestScrollLeft = this.getSlideScrollLeft(this.pageStarts[closestPage]);
      const pageScrollLeft = this.getSlideScrollLeft(slideIndex);

      return Math.abs(scrollLeft - pageScrollLeft) < Math.abs(scrollLeft - closestScrollLeft)
        ? pageIndex
        : closestPage;
    }, 0);
  }

  get gap() {
    const styles = window.getComputedStyle(this.slider);

    return parseFloat(styles.columnGap || styles.gap) || 0;
  }

  get visibleSlidesCount() {
    const slideWidth = this.slides[0]?.offsetWidth || 1;
    const styles = window.getComputedStyle(this.slider);
    const paddingInline =
      (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
    const visibleWidth = this.slider.clientWidth - paddingInline;

    return Math.max(1, Math.floor((visibleWidth + this.gap) / (slideWidth + this.gap)));
  }

  get pageStarts() {
    const visibleSlidesCount = this.visibleSlidesCount;
    const lastStart = Math.max(0, this.slides.length - visibleSlidesCount);
    const starts = [];

    for (let index = 0; index < this.slides.length; index += visibleSlidesCount) {
      starts.push(Math.min(index, lastStart));
    }

    return [...new Set(starts)];
  }

  get maxScrollLeft() {
    return Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
  }

  getSlideSnapScrollLeft(slide) {
    const sliderLeft = this.slider.getBoundingClientRect().left;
    const slideLeft = slide.getBoundingClientRect().left - sliderLeft;
    const centeredLeft =
      this.slider.scrollLeft + slideLeft - (this.slider.clientWidth - slide.offsetWidth) / 2;

    return Math.max(0, Math.min(centeredLeft, this.maxScrollLeft));
  }

  getSlideScrollLeft(index) {
    const slide = this.slides[index];
    const paddingLeft = parseFloat(window.getComputedStyle(this.slider).paddingLeft) || 0;

    return Math.max(0, Math.min(slide.offsetLeft - paddingLeft, this.maxScrollLeft));
  }

  goToPage(page) {
    const nextPage = Math.max(0, Math.min(page, this.pageStarts.length - 1));

    this.slider.scrollTo({
      left: this.getSlideScrollLeft(this.pageStarts[nextPage]),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  update() {
    const pageStarts = this.pageStarts;
    const page = this.activePage;
    const hasPages = pageStarts.length > 1;

    this.controls?.toggleAttribute('hidden', !hasPages);
    this.prevButton?.toggleAttribute('disabled', page === 0);
    this.nextButton?.toggleAttribute('disabled', page === pageStarts.length - 1);

    this.dots.forEach((dot, dotIndex) => {
      const isVisible = dotIndex < pageStarts.length;
      const isActive = dotIndex === page;

      dot.hidden = !isVisible;
      dot.toggleAttribute('aria-hidden', !isVisible);
      dot.dataset.page = dotIndex;
      dot.classList.toggle('is-active', isActive);

      if (isActive) {
        dot.setAttribute('aria-current', 'page');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('pdp-featured-products-slider')) {
  customElements.define('pdp-featured-products-slider', PdpFeaturedProductsSlider);
}
